'use client';
import dynamic from 'next/dynamic';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { Modal } from '@/components/reactstrap';
import { calculateAgeFromBob, calculateAgeInMonthsWithTwoDates } from '@/utils/lm_utilities';
import { formulateChartUrl } from '@/utils/intermediary_url_service';
import useLocalePath from '@/hooks/useLocalePath';
import { localStorageMap } from '@/utils/maps';
import { getFamilyMembers } from '@/utils/user_service';
import FamilyMemberForm from '../familyMemberForm';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
const BoyIcon = dynamic(() => import('@/components/icons').then((module) => ({ default: module.BoyIcon })), {
  ssr: false,
});
const GirlIcon = dynamic(() => import('@/components/icons').then((module) => ({ default: module.GirlIcon })), {
  ssr: false,
});
import { CalenderIcon } from '@/components/icons';
import StyledDatePicker from '../StyledDatePicker';
import dayjs from 'dayjs';
import safeWindow from '@/utils/safeWindow';
import { useTranslation } from '@/contexts/TranslationContext';
const Button = dynamic(() => import('reactstrap').then((module) => ({ default: module.Button })));
const ButtonGroup = dynamic(() => import('reactstrap').then((module) => ({ default: module.ButtonGroup })));
const MainForm = () => {
  const session = useSessionContext();
  const { t } = useTranslation();
  const localePath = useLocalePath();
  const formRef = useRef(null);

  const [showFmModal, setShowFmModal] = useState(false);

  const [showDob, setShowDob] = useState(true);

  const [formData, setFormData] = useState({
    // dob: new Date().toLocaleDateString('en-CA'),
    dob: null,
    gender: 'm',
    md: new Date().toLocaleDateString('en-CA'),
    member_id: null,
  });

  const [chartType, setchartType] = useState('net-worth');

  // eslint-disable-next-line no-unused-vars
  const [userInfo, setUserInfo] = useState({
    email: '',
    id: '',
    token: '',
  });

  const [familyMembersList, setFamilyMembersList] = useState([]);

  const [hideHeadChartType, sethideHeadChartType] = useState(true);
  const [hideBMIChartType, sethideBMIChartType] = useState(false);

  const [validations, setValidations] = useState(null);
  const [datePickerValidation, setDatePickerValidate] = useState('');

  const scrollToForm = () => {
    if (!formRef.current) return;
    formRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddFamButton = (e) => {
    e.preventDefault();
    setShowFmModal(true);
  };
  const handleClose = () => {
    setShowFmModal(false);
    safeWindow.location.reload();
  };

  const handleInputChange = (event) => {
    const target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'dob') {
      // Disable future dates
      const currentDate = new Date().toISOString().split('T')[0];
      // if (value > currentDate) {
      //   target.value = currentDate;
      //   value = currentDate;
      //   setDatePickerValidate('Cannot calculate measurements for future dates. Please select a valid date of birth.');
      // } else {
      //   setDatePickerValidate(null);
      // }

      const ageYears = calculateAgeFromBob(value); // assuming this returns age in years
      if (ageYears < 15 || ageYears > 100) {
        setValidations('Age must be between 15 and 100 years. Please enter a valid date of birth.');
        return; // early exit—do not apply invalid DOB to formData
      } else {
        setValidations(null);
      }

      // age-in-months logic (only if dob is valid)
      let calculated_age = calculateAgeInMonthsWithTwoDates(value, formData.md);
      sethideHeadChartType(calculated_age > 24);
      sethideBMIChartType(calculated_age < 24);
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle date change for DatePicker
  const handleDateChange = (newValue) => {
    const value = newValue ? newValue.format('YYYY-MM-DD') : '';
    handleInputChange({
      target: { name: 'dob', value: value },
    });
  };

  const handleFamilyMemberChange = (e) => {
    let member = familyMembersList.filter((member) => member.id == e.target.value)[0];
    if (member) {
      setShowDob(false);
      let dob = new Date(member.dob).toLocaleDateString('en-CA');
      let calculated_age = calculateAgeInMonthsWithTwoDates(dob, formData.md);
      sethideHeadChartType(calculated_age > 24);
      sethideBMIChartType(calculated_age < 24);
      setFormData({
        ...formData,
        dob: dob,
        gender: member.gender,
        member_id: member.id,
      });

      safeWindow?.sessionStorage.setItem('member_id', member.id);
      safeWindow?.sessionStorage.setItem('member_name', member.name);
      safeWindow?.sessionStorage.setItem('useDates', true);
    }
  };

  const onRadioBtnClick = (chartType) => {
    setchartType(chartType);
  };

  useEffect(() => {
    let presistedDob = safeWindow.localStorage.getItem('lm_previous_dob');
    if (session.doesSessionExist) {
      getFamilyMembers()
        .then((res) => {
          if (res.status === 200) {
            return res.json();
          } else if (res.status === 401 || res.status === 403) {
            safeWindow.localStorage.setItem('lm_previous_path', safeWindow.location.pathname);
            safeWindow.location.href = localePath('/signin');
          } else throw new Error(res.statusText);

          return res.json();
        })
        .then((data) => {
          setFamilyMembersList(data);
        })
        .catch((err) => console.log(err));
    }

    if (presistedDob) {
      setFormData({
        ...formData,
        dob: JSON.parse(presistedDob),
      });
    }
  }, [session?.doesSessionExist]);

  const validateDoB = () => {
    const { dob } = formData;
    let isValid = true;
    let validationMsg;
    if (!dob) {
      validationMsg = 'Please select a date of birth';
      isValid = false;
    }
    if (dob && calculateAgeFromBob(dob) > 100) {
      // validationMsg = 'Sorry, we only have data for 0-100year olds but soon we'll have data for all ages';
      validationMsg = 'Sorry, we only have data for 0-100 year olds';
      isValid = false;
    }
    if (!isValid) {
      setValidations(validationMsg);
    }

    return isValid;
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (!validateDoB()) {
      scrollToForm();
      return;
    }
    let dob = new Date(formData.dob).toLocaleDateString('en-CA');
    safeWindow.sessionStorage.setItem('dob',dob)
    safeWindow.sessionStorage.setItem('useDates', true);
    safeWindow.localStorage.setItem(localStorageMap[chartType], JSON.stringify(formData));
    // presist DoB
    safeWindow.localStorage.setItem('lm_previous_dob', JSON.stringify(formData.dob));
    let calculated_age = calculateAgeInMonthsWithTwoDates(formData.dob, formData.md);
    /*
        Route to appropriate path
         */
    let url = localePath(formulateChartUrl(formData, chartType, calculated_age));
    if (!validations) {
      safeWindow.location.href = url;
    }
  };
  return (
    <div className="getting-started" ref={formRef}>
      <Modal centered isOpen={showFmModal} onHide={handleClose}>
        <FamilyMemberForm closeBtn={true} handleClose={handleClose} />
        {/* <button onClick={handleClose} color="btn btn-primary add-family-member-btn">
          Close
        </button> */}
      </Modal>
      <form>
        {session.doesSessionExist && (
          <div className="row py-2 justify-content-center">
            <div className="col-10 ps-0">
              <select
                type="select"
                id="member"
                name="member_measurement"
                className="form-select form-control h-100"
                onChange={handleFamilyMemberChange}
              >
                <option value="">{t('form.letsMeasure')}</option>
                {familyMembersList.map((el) => {
                  return (
                    <option key={el.id} value={el.id}>
                      {el.name}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-1 d-flex flex-direction-coloumn justify-content-center align-items-center ms-1 ps-1 pe-0">
              <button onClick={handleAddFamButton} className="add-family-member-link">
                <Image
                  priority
                  className="img-fluid"
                  src={'/assets/images/icons/addfamily.svg'}
                  alt="add"
                  height="36"
                  width="36"
                />
              </button>
            </div>
          </div>
        )}
        {showDob && (
          <div className="row">
            <div className="col-12">
              <span className="sub-title-chart d-block text-start mb-2">{t('form.gender')}</span>
            </div>
            <div className="d-flex justify-content-center w-100 m-auto" style={{ maxWidth: '600px' }}>
              <div className="d-flex flex-column align-items-center w-100">
                <label className={`main-checkbox m-auto w-100 ${formData.gender === 'm' ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="m"
                    defaultChecked={formData.gender === 'm'}
                    onChange={handleInputChange}
                  />
                  <div className="d-flex align-items-center flex-column box ">
                    <div className="d-flex align-items-center">
                      <div className="boy-radio circle-bg ml-2 ">
                        <BoyIcon color={formData.gender === 'm' ? '#fff' : '#492B17'} />
                      </div>
                      <span>{t('form.men')}</span>
                    </div>
                    <div className="bottom-line" />
                  </div>
                </label>
              </div>

              <div className="d-flex flex-column align-items-center w-100">
                <label className={`main-checkbox m-auto w-100 ${formData.gender === 'f' ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="f"
                    defaultChecked={formData.gender === 'f'}
                    onChange={handleInputChange}
                  />
                  <div className="d-flex align-items-center flex-column box ">
                    <div className="d-flex align-items-center">
                      <div className="boy-radio circle-bg ml-2">
                        <GirlIcon color={formData.gender === 'f' ? '#fff' : '#492B17'} />
                      </div>
                      <span>{t('form.women')}</span>
                    </div>
                    <div className="bottom-line" />
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
        {showDob && (
          <div className="row py-2">
            <div className="col mb-2">
              <label htmlFor="dob" className="col-12">
                <span className="sub-title-chart d-block text-start">{t('form.dateOfBirth')}</span>
              </label>
            </div>
            <div
              className="col-12 d-flex i_measurement-wrapper position-relative"
              style={{ justifyContent: 'space-around' }}
            >
              <div className="col">
                <div className="input-wrapper" style={{ background: 'transparent', borderColor: '#7C5032' }}>
                  <StyledDatePicker
                    id="dob"
                    name="dob"
                    value={formData.dob ? dayjs(formData.dob) : null}
                    onChange={handleDateChange}
                    className={`dob form-control ${(validations || datePickerValidation) && 'error-border'}`}
                    color="white"
                  />
                </div>
              </div>

              {validations && <span className="error-text">{validations}</span>}
              {datePickerValidation && <span className="error-text">{datePickerValidation}</span>}
            </div>
          </div>
        )}
        <div className="row py-3 radio-toolbar">
          <ButtonGroup className="cardButton-grid">
            <Button
              className="cardButton"
              onClick={() => onRadioBtnClick('net-worth')}
              active={chartType === 'net-worth'}
            >
              {/* <section className="weight" ></section> */}
              <div className={`${chartType === 'net-worth' ? 'circle-active' : 'circle'}   mb-3`}>
                <Image
                  priority
                  src={'/assets/images/icons/NetWorth-Brown.svg'}
                  alt="weight measurement icon"
                  width="40"
                  height="40"
                />
              </div>
              <div>{t('nav.netWorth')}</div>
            </Button>
            <Button
              className="cardButton"
              onClick={() => onRadioBtnClick('annual-income')}
              active={chartType === 'annual-income'}
            >
              <div className={`${chartType === 'annual-income' ? 'circle-active' : 'circle'}   mb-3`}>
                <Image
                  priority
                  src={'/assets/images/icons/Income-Brown.svg'}
                  alt="measure icon"
                  width="40"
                  height="40"
                />
              </div>
              <div>{t('nav.income')}</div>
            </Button>

            <Button
              className="cardButton"
              onClick={() => onRadioBtnClick('retirement-fund')}
              active={chartType === 'retirement-fund'}
            >
              <div className={`${chartType === 'retirement-fund' ? 'circle-active' : 'circle'}   mb-3`}>
                <Image
                  priority
                  src={'/assets/images/icons/piggyBank-Brown.svg'}
                  alt="measure icon"
                  width="40"
                  height="40"
                />
              </div>
              <div>{t('nav.retirement')}</div>
            </Button>
            {/* {!hideBMIChartType && (
              <Button
                className="cardButton"
                onClick={() => onRadioBtnClick('bmi-charts')}
                active={chartType === 'bmi-charts'}
              >
                <div className="circle mb-3">
                  <Image
                    priority
                    src={'/assets/images/icons/bmi.svg'}
                    alt="bmi measurement icon"
                    width="40"
                    height="40"
                  />
                </div>
                <div>BMI</div>
              </Button>
            )} */}

            {/* {!hideHeadChartType && (
              <Button
                className="ms-2 cardButton"
                onClick={() => onRadioBtnClick('head-charts')}
                active={chartType === 'head-charts'}
              >
                <div className="circle mb-3">
                  <Image
                    priority
                    src={'/assets/images/icons/calculator.svg'}
                    alt="head measurement calculate icon"
                    width="40"
                    height="40"
                  />
                </div>
                <div>Head</div>
              </Button>
            )} */}
          </ButtonGroup>
        </div>
        <div className="row mt-5">
          <div className="col">
            <button className={`btn btn-primary viewChartChange`} onClick={handleFormSubmit}>
              {t('form.startMeasuring')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MainForm;
